import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import { xhrRequestConfig } from '@/js/utils/api'

// Cookie helpers
function getCookie (name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

function setCookie (name, value, days = 1) {
  const d = new Date()
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/`
}

const COOKIE_KEY = 'gwp_dismissed'

/**
 * Read the dismissed GWP keys from cookie.
 * Returns a Set of GWP keys the customer has opted-out of, e.g. {"gwp1","gwp3"}
 */
function getDismissed () {
  const raw = getCookie(COOKIE_KEY)
  return raw ? new Set(raw.split(',')) : new Set()
}

function setDismissed (set) {
  setCookie(COOKIE_KEY, [...set].join(','))
}

function getPropertyValue (properties, propertyName) {
  if (!properties) return null
  if (properties[propertyName]) return properties[propertyName]

  if (Array.isArray(properties)) {
    const property = properties.find((item) => {
      if (Array.isArray(item)) return item[0] === propertyName
      if (item && typeof item === 'object') return item.name === propertyName || item.key === propertyName
      return false
    })

    if (Array.isArray(property)) return property[1]
    if (property && typeof property === 'object') return property.value
  }

  return null
}

export default {
  props: {
    /**
     * Gift settings are passed from the Liquid template as a JSON prop.
     * Shape: { gwp1: {...}, gwp2: {...}, gwp3: {...} }
     */
    giftSettings: {
      type: Object,
      required: true
    }
  },

  setup (props) {
    const cart = ref(null)
    const isLoading = ref(false)
    const dismissed = ref(getDismissed())
    const busyVariants = ref(new Set()) // variant IDs currently being added/removed
    const cartData = ref(null)

    // Update cart counter badge in header
    function updateCartCounter () {
      if (typeof document === 'undefined') return
      const count = cart.value && typeof cart.value.item_count === 'number'
        ? cart.value.item_count
        : 0
      const counterIds = ['cart-counter', 'CartCount']
      counterIds.forEach((id) => {
        const el = document.getElementById(id)
        if (el) el.textContent = String(count)
      })
    }

    function syncAjaxCartDrawer () {
      if (typeof window === 'undefined') return
      if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
        window.ajaxCart.load()
      }
    }

    // ─── Fetch cart ────────────────────────────────────────────────
    async function fetchCart () {
      isLoading.value = true
      try {
        const [cartResponse, cartDataResponse] = await Promise.all([
          axios.get(`${window.Shopify.routes.root}cart.js`),
          axios.get(`${window.Shopify.routes.root}cart?view=data`)
        ])
        cart.value = cartResponse.data
        cartData.value = cartDataResponse.data
        updateCartCounter()
        syncAjaxCartDrawer()
      } catch (e) {
        console.error('[FreeGiftCard] fetchCart error', e)
      } finally {
        isLoading.value = false
      }
    }

    // ─── Cart helpers ──────────────────────────────────────────────
    const cartItemProductIds = computed(() => {
      if (!cart.value) return []
      return cart.value.items.map(i => i.product_id)
    })

    const giftCartItems = computed(() => {
      if (!cart.value) return []
      return cart.value.items.filter(item => isFreeGiftCartItem(item))
    })

    const cartTotal = computed(() => {
      if (!cart.value) return 0
      // Subtract any existing GWP items (price 0) to avoid inflating total
      return cart.value.items.reduce((sum, item) => {
        if (item.properties && item.properties._gwp) return sum
        return sum + item.final_line_price
      }, 0)
    })

    // ─── Check which cart product handles belong to trigger collections ──
    // GWP1 collection product IDs are fetched via Storefront API at mount
    function isGwp1Qualified () {
      const cfg = props.giftSettings.gwp1
      if (!cfg || !cfg.enabled) return false
      const triggerIds = cfg.triggerCollectionIds || []
      if (!triggerIds.length) return false
      const items = cartData.value && cartData.value.items ? cartData.value.items : []
      return items.some((item) => {
        if (isFreeGiftCartItem(item)) return false
        const collectionIds = item.collection_ids || []
        return collectionIds.some(id => triggerIds.includes(id))
      })
    }

    function isGwp2Qualified () {
      const cfg = props.giftSettings.gwp2
      if (!cfg || !cfg.enabled) return false
      const threshold = parseFloat(cfg.threshold) * 100 // convert dollars to cents
      return cartTotal.value >= threshold
    }

    function isGwp3Qualified () {
      const cfg = props.giftSettings.gwp3
      if (!cfg || !cfg.enabled) return false
      const triggerIds = cfg.triggerProductIds || []
      if (!triggerIds.length) return false
      return cartItemProductIds.value.some(pid => triggerIds.includes(pid))
    }

    // ─── Qualification state ──────────────────────────────────────
    const qualifiedGifts = computed(() => {
      const gifts = []
      if (isGwp1Qualified()) gifts.push('gwp1')
      if (isGwp2Qualified()) gifts.push('gwp2')
      if (isGwp3Qualified()) gifts.push('gwp3')
      return gifts
    })

    /**
     * The FIRST qualified gift is auto-added.
     * Any subsequent qualified gifts are optional (shown with Add to Cart button).
     */
    const autoGiftKey = computed(() => qualifiedGifts.value[0] || null)
    const optionalGiftKeys = computed(() => qualifiedGifts.value.slice(1))

    // ─── Resolve gift product data & variant ──────────────────────
    function getGiftConfig (key) {
      return props.giftSettings[key] || null
    }

    function giftVariantId (key) {
      const cfg = getGiftConfig(key)
      return cfg ? cfg.variantId : null
    }

    function giftTitle (key) {
      const cfg = getGiftConfig(key)
      return cfg ? cfg.title : 'Claim your free gift'
    }

    function giftProduct (key) {
      const cfg = getGiftConfig(key)
      return cfg ? cfg.product : null
    }

    function giftLineTitle (key) {
      const product = giftProduct(key)
      if (product && product.productTitle) return product.productTitle
      return giftTitle(key)
    }

    function isFreeGiftCartItem (item) {
      return Boolean(
        getPropertyValue(item && item.properties, '_gwp') ||
        getPropertyValue(item && item.properties, '_free_gift') ||
        getPropertyValue(item && item.properties, '_gift_title')
      )
    }

    function giftCartItemRule (item) {
      return getPropertyValue(item && item.properties, '_gwp')
    }

    function giftCartItemTitle (item) {
      const giftRule = giftCartItemRule(item)
      if (giftRule && getGiftConfig(giftRule)) return giftTitle(giftRule)
      return getPropertyValue(item && item.properties, '_gift_title') || 'Claim your free gift'
    }

    function giftCartItemProductTitle (item) {
      return item.product_title || item.title || getPropertyValue(item && item.properties, '_gift_title') || giftCartItemTitle(item)
    }

    function giftCartItemImage (item) {
      return item.image || item.featured_image || null
    }

    // ─── Check if a GWP variant is already in cart ────────────────
    function isGiftInCart (key) {
      if (!cart.value) return false
      const vid = giftVariantId(key)
      if (!vid) return false
      return cart.value.items.some(
        i => i.variant_id === vid && i.properties && i.properties._gwp === key
      )
    }

    // ─── Cart mutations ───────────────────────────────────────────
    async function addGiftToCart (key) {
      const vid = giftVariantId(key)
      if (!vid || isGiftInCart(key)) return
      if (busyVariants.value.has(vid)) return

      busyVariants.value.add(vid)
      try {
        await axios.post(
          `${window.Shopify.routes.root}cart/add.js`,
          {
            id: vid,
            quantity: 1,
            properties: { _gwp: key, _free_gift: true, _gift_title: giftLineTitle(key) }
          },
          xhrRequestConfig
        )
        // Un-dismiss if it was previously dismissed
        dismissed.value.delete(key)
        setDismissed(dismissed.value)
        await fetchCart()
      } catch (e) {
        console.error(`[FreeGiftCard] addGiftToCart(${key}) error`, e)
      } finally {
        busyVariants.value.delete(vid)
      }
    }

    async function removeGiftFromCart (key, { optOut = false } = {}) {
      if (!cart.value) return
      const vid = giftVariantId(key)
      if (!vid) return

      const item = cart.value.items.find(
        i => i.variant_id === vid && i.properties && i.properties._gwp === key
      )
      if (!item) return

      if (busyVariants.value.has(vid)) return
      busyVariants.value.add(vid)

      try {
        await axios.post(
          `${window.Shopify.routes.root}cart/change.js`,
          { id: item.key, quantity: 0 },
          xhrRequestConfig
        )
        if (optOut) {
          dismissed.value.add(key)
          setDismissed(dismissed.value)
        }
        await fetchCart()
      } catch (e) {
        console.error(`[FreeGiftCard] removeGiftFromCart(${key}) error`, e)
      } finally {
        busyVariants.value.delete(vid)
      }
    }

    // ─── Reconcile: auto-add / auto-remove ────────────────────────
    async function reconcile () {
      if (!cart.value) return

      const qualified = qualifiedGifts.value
      const allKeys = ['gwp1', 'gwp2', 'gwp3']

      // 1. Remove gifts that are in cart but no longer qualified
      for (const key of allKeys) {
        if (!qualified.includes(key) && isGiftInCart(key)) {
          await removeGiftFromCart(key)
        }
      }

      // 2. Auto-add every qualified gift because the separate gift selector UI is hidden.
      for (const key of qualified) {
        if (!isGiftInCart(key) && giftVariantId(key)) {
          await addGiftToCart(key)
        }
      }
    }

    // ─── Customer opt-out (remove + cookie) ───────────────────────
    function dismissGift (key) {
      removeGiftFromCart(key, { optOut: true })
    }

    async function dismissGiftItem (item) {
      if (!item || !item.key) return
      try {
        await axios.post(
          `${window.Shopify.routes.root}cart/change.js`,
          { id: item.key, quantity: 0 },
          xhrRequestConfig
        )
        await fetchCart()
      } catch (e) {
        console.error('[FreeGiftCard] dismissGiftItem error', e)
      }
    }

    // ─── Re-claim a previously dismissed optional gift ────────────
    function claimGift (key) {
      dismissed.value.delete(key)
      setDismissed(dismissed.value)
      addGiftToCart(key)
    }

    // ─── Lifecycle ────────────────────────────────────────────────
    onMounted(async () => {
      await fetchCart()
      await reconcile()
    })

    // Watch for qualification changes (e.g. if cart is updated externally)
    watch(qualifiedGifts, async () => {
      await reconcile()
    })

    // ─── Template helpers ─────────────────────────────────────────
    function formatMoney (cents) {
      if (cents == null) return '$0.00'
      return '$' + (cents / 100).toFixed(2)
    }

    return {
      cart,
      isLoading,
      dismissed,
      busyVariants,

      qualifiedGifts,
      autoGiftKey,
      optionalGiftKeys,
      giftCartItems,

      getGiftConfig,
      giftTitle,
      giftProduct,
      giftVariantId,
      isGiftInCart,
      giftCartItemTitle,
      giftCartItemProductTitle,
      giftCartItemImage,

      addGiftToCart,
      removeGiftFromCart,
      dismissGift,
      dismissGiftItem,
      claimGift,
      reconcile,
      formatMoney
    }
  }
}
